import { alpha } from "@mui/material/styles";

// ----------------------------------------------------------------------

export type ColorSchema =
  | "primary"
  | "secondary"
  | "info"
  | "success"
  | "warning"
  | "error"
  | "quaternary"
  | "taskboard";

declare module "@mui/material/styles/createPalette" {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
  interface Palette {
    quaternary: {
      main: string;
      light: string;
      lighter: string;
      darker: string;
      dark: string;
      blue: string;
      darkBlue: string;
      lightGreen: string;
      darkGreen: string;
      success: string;
      customTurquoise: string;
      dodgerBlue: string;
      bottleGreen: string;
      navyBlue: string;
      lightBlue: string;
      ceruleanBlue: string;
      royalBlue: string;
      lightPink: string;
      lightPurple: string;
      lightOrange: string;
      lightParrot: string;
      lightYellow: string;
      aliceBlue: string;
      royalNavyBlue: string;
      lavender: string;
      orange: string;
    };

    tertiary: {
      light: string;
      main: string;
    };
    chart: {
      skyBlue: string;
      tealGreen: string;
      sandyYellow: string;
      mintGreen: string;
      coralOrange: string;
    };
  }
}

// SETUP COLORS

export const GREY = {
  0: "#FFFFFF",
  50: "#FBFBFB",
  100: "#F9FAFB",
  200: "#E0E0E0",
  300: "#ACACAC",
  400: "#C4CDD5",
  500: "#888888",
  550: "#A0A0A042",
  600: "#494949",
  700: "#454F5B",
  800: "#212B36",
  900: "#161C24",
  450: "#717171",
};

const PRIMARY = {
  lighter: "#F8D7DA",
  light: "#C75B6A",
  main: "#800000",
  dark: "#660000",
  darker: "#4D0000",
  premiumDarker: "#5C0000",
  contrastText: "#FFFFFF",
};

const SECONDARY = {
  lighter: "#CBA69F",
  light: "#A36457",
  main: "#7C210F",
  dark: "#57170B",
  darker: "#320D06",
  contrastText: "#FFFFFF",
};

const TERTIARY = {
  light: "#D1D7FF",
  main: "#4656B6",
  dark: "#364495",
};
const QUATERNARY = {
  lighter: "#FFCC99",
  light: "#0080FF",
  main: "#0066CC",
  dark: "#004C99",
  darker: "#008B8B",
  contrastText: "#FFFFFF",
  success: "#28A3AA",
  blue: "#6AAEE0",
  darkBlue: "#4E7BBF",
  lightGreen: "#84CACE",
  darkGreen: "#28A2AA",
  customTurquoise: "#4EE2EA",
  ceruleanBlue: "#308fe8",
  bottleGreen: "#406673",
  navyBlue: "#5EACC8",
  lightBlue: "#CBE6F0",
  royalBlue: "#4E7BBF",
  lightPink: "#FFE1F0",
  lightPurple: "#D8D8FF",
  lightOrange: "#FFE9D6",
  lightParrot: "#DDF5F1",
  lightYellow: "#FFF1CD",
  royalNavyBlue: "#252CB6",
  lavender: "#D9DBFF",
  aliceBlue: "#EFF0FF",
  orange: "#FF7A45",
};

const INFO = {
  lighter: "#BFEBFA",
  light: "#89DAF5",
  main: "#3AC1EF",
  dark: "#11A3D4",
  darker: "#0D7FA5",
  contrastText: "#FFFFFF",
};

const SUCCESS = {
  lighter: "#D8FBDE",
  light: "#86E8AB",
  main: "#36B37E",
  dark: "#1B806A",
  darker: "#0A5554",
  contrastText: "#FFFFFF",
};

const WARNING = {
  lighter: "#FFF5CC",
  light: "#FFD666",
  main: "#FFAB00",
  dark: "#B76E00",
  darker: "#7A4100",
  contrastText: GREY[800],
};

const ERROR = {
  lighter: "#FFCEC7",
  light: "#FF9282",
  main: "#E34932",
  dark: "#942B1B",
  darker: "#63160A",
  contrastText: "#FFFFFF",
};

const TASKBOARD = {
  mustardYellow: "#DDAB28",
  paleYellow: "#FFEAB3",
  ivoryYellow: "#FFF6E0",

  candyPink: "#FFD4EB",
  pink: "#ED6EB2",
  lightPink: "#FFEDF7",
  mustardLight: "#95881A",
  mustardLighter: "#8B951A",
};

const CHART = {
  skyBlue: "#a5aee8",
  tealGreen: "#7ac0b0",
  sandyYellow: "#f1d278",
  mintGreen: "#aeee",
  coralOrange: "#f3a77d",
};

const COMMON = {
  common: { black: "#000000", white: "#FFFFFF" },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  tertiary: TERTIARY,
  quaternary: QUATERNARY,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  taskboard: TASKBOARD,
  chart: CHART,
  divider: alpha(GREY[500], 0.24),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export function palette(themeMode: "light" | "dark") {
  const light = {
    ...COMMON,
    mode: "light",
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: { paper: "#FFFFFF", default: "#FFFFFF", neutral: GREY[200] },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  } as const;

  const dark = {
    ...COMMON,
    mode: "dark",
    text: {
      primary: "#FFFFFF",
      secondary: GREY[500],
      disabled: GREY[600],
    },
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: alpha(GREY[500], 0.16),
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
  } as const;

  return themeMode === "light" ? light : dark;
}
